import { useState } from "react"
import { router } from "@inertiajs/react"
import { RefreshCw } from "lucide-react"
import { AdminLayout } from "@/components/layouts/admin-layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

type DatabaseStatus = "up" | "down" | "unknown"

type DatabaseCheck = {
  name: string
  status: DatabaseStatus
  latencyMs: number | null
  error: string | null
}

type BuildInfo = {
  revision: string
  environment: string
  migrationVersion: number | null
  rubyVersion: string
  railsVersion: string
  bootedAt: string | null
}

type Props = {
  generatedAt: string
  build: BuildInfo
  databases: DatabaseCheck[]
}

function formatDateTime(value: string | null) {
  if (!value) return "—"
  return new Intl.DateTimeFormat("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value))
}

const STATUS_STYLES: Record<DatabaseStatus, string> = {
  up: "bg-status-available",
  down: "bg-destructive",
  unknown: "bg-muted-foreground",
}

function statusLabel(status: DatabaseStatus) {
  return t(`ui.react.pages.admin.system_status.index.status_${status}`)
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-xs">{value}</span>
    </div>
  )
}

export default function AdminSystemStatusIndex({ generatedAt, build, databases }: Props) {
  const [refreshing, setRefreshing] = useState(false)

  const refresh = () => {
    setRefreshing(true)
    router.reload({ onFinish: () => setRefreshing(false) })
  }

  const tk = (key: string) => t(`ui.react.pages.admin.system_status.index.${key}`)

  return (
    <AdminLayout>
      <MainContainer>
        <PageHeader
          title={tk("title")}
          description={tk("description")}
          actions={
            <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
              <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
              {tk("refresh")}
            </Button>
          }
        />

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{tk("build_title")}</CardTitle>
              <CardDescription>{`${tk("generated_at")}: ${formatDateTime(generatedAt)}`}</CardDescription>
            </CardHeader>
            <CardContent className="divide-y">
              <InfoRow label={tk("revision")} value={build.revision} />
              <InfoRow label={tk("environment")} value={build.environment} />
              <InfoRow
                label={tk("migration")}
                value={build.migrationVersion ? String(build.migrationVersion) : "—"}
              />
              <InfoRow label={tk("ruby")} value={build.rubyVersion} />
              <InfoRow label={tk("rails")} value={build.railsVersion} />
              <InfoRow label={tk("booted_at")} value={formatDateTime(build.bootedAt)} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{tk("databases_title")}</CardTitle>
            </CardHeader>
            <CardContent className="divide-y">
              {databases.map((database) => (
                <div
                  key={database.name}
                  className="flex items-center justify-between gap-4 py-2.5 text-sm"
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className={cn("h-2 w-2 shrink-0 rounded-full", STATUS_STYLES[database.status])}
                    />
                    <span className="font-medium capitalize">{database.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {database.latencyMs != null && (
                      <span className="font-mono text-xs text-muted-foreground">
                        {database.latencyMs} ms
                      </span>
                    )}
                    <Badge variant="outline" className="capitalize">
                      {statusLabel(database.status)}
                    </Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </MainContainer>
    </AdminLayout>
  )
}
